import { useState } from 'react';
import {
  Avatar,
  Button,
  DataTable,
  EmptyState,
  ErrorState,
  FormGrid,
  FormPanel,
  ListRow,
  PageHeader,
  SelectField,
  TableCard,
  Tag,
  TextField,
  type Column,
} from '@sparkery/ui';
import type { TeamMember } from '@sparkery/contracts';
import { useInviteTeamMember, useTeam } from '../../lib/workspace.ts';
import './team.css';

/**
 * The people with access to a partner account.
 *
 * Read-only for now, deliberately: WorkOS owns users and memberships, so inviting and
 * removing are calls to its API rather than rows in this database. Rendering controls
 * that cannot work yet would be worse than saying so.
 */
export function TeamView() {
  const team = useTeam();
  const data = team.data;
  const invite = useInviteTeamMember();
  const [openInvite, setOpenInvite] = useState(false);

  const columns: Column<TeamMember>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: (row) => (
        <span className="teamPerson">
          <Avatar initials={row.initials} name={row.name} size="sm" />
          <span className="teamPersonName">
            <strong>{row.name}</strong>
            {row.isSelf ? <span className="teamYou">You</span> : null}
          </span>
        </span>
      ),
    },
    { id: 'email', header: 'Email', secondary: true, cell: (row) => row.email },
    {
      id: 'type',
      header: 'Type',
      // Solid, not a wash: an account type is not one more order state, and the
      // prototype fills it for exactly that reason.
      cell: (row) => (
        <Tag tone={row.type === 'admin' ? 'strong' : 'neutral'}>
          {row.type === 'admin' ? 'Admin' : 'Standard'}
        </Tag>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Team"
        subtitle={
          data ? `People with access to ${data.partnerName}` : 'People with access to this account'
        }
        actions={
          data?.canManage ? (
            <Button variant="primary" onClick={() => setOpenInvite(true)}>
              Invite
            </Button>
          ) : undefined
        }
      />

      {team.isError ? (
        <ErrorState
          title="Could not load your team"
          {...(team.error instanceof Error ? { detail: team.error.message } : {})}
          onRetry={() => void team.refetch()}
        />
      ) : null}

      <TableCard
        title="Team"
        meta={
          data
            ? `${data.members.length} ${data.members.length === 1 ? 'member' : 'members'}`
            : undefined
        }
      >
        <DataTable
          caption="Team members"
          columns={columns}
          rows={data?.members ?? []}
          rowKey={(row) => row.userId}
          loading={team.isPending}
          mobileCard={(row) => (
            <ListRow
              lead={<Avatar initials={row.initials} name={row.name} size="sm" />}
              title={
                <>
                  {row.name}
                  {row.isSelf ? <span className="teamYou">You</span> : null}
                </>
              }
              meta={row.email}
              trailing={
                <Tag tone={row.type === 'admin' ? 'strong' : 'neutral'}>
                  {row.type === 'admin' ? 'Admin' : 'Standard'}
                </Tag>
              }
            />
          )}
          empty={
            <EmptyState
              title="Nobody has access yet"
              body="Invite a colleague to share this account."
            />
          }
        />
        {data?.canManage ? null : (
          <p className="teamNote">Only account admins can change who has access.</p>
        )}
      </TableCard>

      {openInvite ? (
        <TeamInvitePanel
          busy={invite.isPending}
          error={invite.error instanceof Error ? invite.error.message : null}
          onCancel={() => setOpenInvite(false)}
          onInvite={(input) =>
            invite.mutate(input, {
              onSuccess: () => setOpenInvite(false),
            })
          }
        />
      ) : null}
    </>
  );
}

function TeamInvitePanel({
  busy,
  error,
  onCancel,
  onInvite,
}: {
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onInvite: (input: { email: string; role: 'partner_user' | 'partner_admin' }) => void;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'partner_user' | 'partner_admin'>('partner_user');

  return (
    <FormPanel
      title="Invite a user"
      confirmLabel={busy ? 'Sending...' : 'Send invite'}
      confirmDisabled={!email.trim() || busy}
      onCancel={onCancel}
      onConfirm={() => onInvite({ email: email.trim(), role })}
      {...(error ? { note: error } : {})}
    >
      <FormGrid columns="1fr 180px">
        <TextField
          id="team-user-email"
          label="Email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.currentTarget.value)}
          placeholder="john@bodyline.com"
        />
        <SelectField
          id="team-user-role"
          label="Role"
          value={role}
          onChange={(event) => setRole(event.currentTarget.value as typeof role)}
          options={[
            { value: 'partner_user', label: 'Standard' },
            { value: 'partner_admin', label: 'Admin' },
          ]}
        />
      </FormGrid>
    </FormPanel>
  );
}
